import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Placeholder from "@/components/Placeholder";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FolderOpen, Download, FileText, Loader2 } from "lucide-react";
import MarketList from "@/portal/assets/MarketList";
import ResearchPrompts from "@/portal/assets/ResearchPrompts";
import PositioningFrameworks from "@/portal/assets/PositioningFrameworks";
import HiringKit from "@/portal/assets/HiringKit";
import BreakdownLibrary from "@/portal/assets/BreakdownLibrary";

const ASSETS = [
  { key: "market-list", title: "Pre-Researched Market List", desc: "10 funded niches already paying to solve these problems.", source: "02-market-list.md", ready: true, pdf: true },
  { key: "research-prompts", title: "Pain-Point Research Prompts", desc: "Copy-paste prompts that surface what your market pays for.", source: "03-research-prompts.md", ready: true, pdf: true },
  { key: "positioning", title: "Premium Positioning Frameworks", desc: "Fill your One-Page Offer Doc and export it.", source: "04-positioning-frameworks.md", ready: true, pdf: true },
  { key: "hiring", title: "Freelancer Hiring System", desc: "Job post template, filtering, interview, and ownership clause.", source: "05-freelancer-hiring-kit.md", ready: true, pdf: true },
  { key: "breakdowns", title: "Offer Breakdown Library", desc: "Six complete offer blueprints, dissected end to end.", source: "07-offer-breakdown-library.md", ready: true, pdf: true },
];

function AssetBody({ asset }) {
  if (asset.key === "market-list") return <MarketList />;
  if (asset.key === "research-prompts") return <ResearchPrompts />;
  if (asset.key === "positioning") return <PositioningFrameworks />;
  if (asset.key === "hiring") return <HiringKit />;
  if (asset.key === "breakdowns") return <BreakdownLibrary />;
  return <div className="prose-weekend"><Placeholder>content from {asset.source}</Placeholder></div>;
}

export default function AssetVault() {
  const [open, setOpen] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [params, setParams] = useSearchParams();

  const downloadPdf = async (asset) => {
    if (!asset.pdf) {
      setOpen(asset);
      setTimeout(() => window.print(), 500);
      return;
    }
    setDownloading(asset.key);
    try {
      const res = await api.get(`/assets/${asset.key}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${asset.title.replace(/[^a-z0-9]+/gi, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const downloadZip = async () => {
    setDownloading("zip");
    try {
      const res = await api.get(`/assets/zip`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Weekend-AI-Offer-Asset-Vault.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download the ZIP. Please try again.");
    } finally {
      setDownloading(null);
    }
  };

  const openKey = params.get("open");
  // Deep-link support: /portal/assets?open=research-prompts opens that asset.
  useEffect(() => {
    if (openKey) {
      const match = ASSETS.find((a) => a.key === openKey);
      if (match) setOpen(match);
      params.delete("open");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  return (
    <div data-testid="asset-vault-page">
      <div className="eyebrow" style={{ color: "#2038E6" }}>Asset Vault</div>
      <h1 className="h-display" style={{ fontSize: "clamp(32px,5vw,48px)", marginTop: 8 }}>Your Deliverables</h1>
      <p style={{ color: "var(--muted)", marginTop: 10 }}>Open any asset to use it. Download a PDF to keep it.</p>

      <button className="btn-cobalt" style={{ marginTop: 16, padding: "11px 20px", fontSize: 13 }} onClick={downloadZip} disabled={downloading === "zip"} data-testid="asset-download-all-zip">
        {downloading === "zip" ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
        {downloading === "zip" ? "Preparing your kit..." : "Download all assets (ZIP)"}
      </button>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16, marginTop: 26 }}>
        {ASSETS.map((a) => (
          <div key={a.key} className="card-paper" data-testid={`asset-card-${a.key}`} style={{ padding: 22, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <FileText size={24} color="#2038E6" />
              {!a.ready && <span className="placeholder-tag" style={{ fontSize: 10 }}>[PLACEHOLDER]</span>}
            </div>
            <h3 className="font-archivo" style={{ fontWeight: 800, fontSize: 18, marginTop: 12 }}>{a.title}</h3>
            <p style={{ color: "var(--muted)", fontSize: 14, marginTop: 6, flex: 1 }}>{a.desc}</p>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="btn-cobalt" style={{ padding: "9px 16px", fontSize: 12 }} onClick={() => setOpen(a)} data-testid={`asset-open-${a.key}`}>
                <FolderOpen size={15} /> Open
              </button>
              <button className="btn-outline" style={{ padding: "9px 16px", fontSize: 12 }} onClick={() => downloadPdf(a)} disabled={downloading === a.key} data-testid={`asset-pdf-${a.key}`}>
                {downloading === a.key ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} PDF
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="card-paper" style={{ maxWidth: 720, maxHeight: "88vh", overflowY: "auto" }}>
          <DialogHeader>
            <DialogTitle className="font-archivo" style={{ fontWeight: 900, textTransform: "uppercase", fontSize: 24 }}>{open?.title}</DialogTitle>
            <DialogDescription className="font-mono-plex" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>{open?.source}</DialogDescription>
          </DialogHeader>
          {open && <AssetBody asset={open} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
